import React, { useState } from 'react';
import { MapPin, ChevronRight } from 'lucide-react';
import type { TeamMember } from '../lib/about';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../lib/i18n';

interface TeamMemberCardProps {
  member: TeamMember;
  rotationDegrees: number;
  onCardClick: (id: string) => void;
  isVisible: boolean;
}

export function TeamMemberCard({ 
  member,
  rotationDegrees,
  onCardClick,
  isVisible
}: TeamMemberCardProps) {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState<any>({});

  React.useEffect(() => {
    const loadTranslations = async () => {
      const homeTranslations = await getTranslation(language, 'home');
      setTranslations(homeTranslations);
    };
    loadTranslations();
  }, [language]);

  const handleClick = () => {
    onCardClick(member.id);
  };

  if (!translations.buttons) {
    return null;
  }

  return (
    <div 
      className={`flip-card h-[475px] transition-opacity duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      onClick={handleClick}
    >
      <div 
        className="flip-card-inner h-full"
        style={{ transform: `rotateY(${rotationDegrees}deg)` }}
      >
        <div className="flip-card-front bg-white rounded-xl shadow-sm p-6 h-full">
          <div className="flex flex-col h-full">
            <div className="relative w-48 h-48 mx-auto mb-6">
              <img 
                src={member.image} 
                alt={member.name[language]}
                className="w-full h-full object-cover object-[50%_35%] team-member-photo rounded-xl"
              />
            </div>

            <div className="text-center flex-grow flex flex-col">
              <h3 className="text-xl font-semibold mb-2 text-gray-800 group-hover:text-bamboo-700 transition-colors duration-300">
                {member.name[language]}
              </h3>
              <p className="text-bamboo-600 text-sm mb-3">
                {member.role[language]}
              </p>
              <div className="flex items-center justify-center text-gray-600 text-sm mb-4">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{member.area[language]}</span>
              </div>
              <p className="text-gray-600 text-sm italic">
                "{member.message[language]}"
              </p>
              <div className="absolute bottom-6 left-0 right-0 flex justify-center">
                <button className="text-bamboo-600 hover:text-bamboo-700 flex items-center gap-1 text-sm group">
                  {translations.buttons.details}
                  <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flip-card-back bg-white rounded-xl shadow-sm p-6 h-full overflow-auto">
          <div className="h-full">
            <h3 className="text-xl font-semibold mb-2 text-gray-800">
              {member.name[language]}
            </h3>
            <h4 className="text-bamboo-600 text-sm mb-4">{translations.team?.profile}</h4>
            <div className="text-gray-600 text-sm [&_ul]:list-none [&_li]:relative [&_li]:pl-4 [&_li]:mb-3 [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:content-['•'] [&_li]:before:text-bamboo-600">
              <div dangerouslySetInnerHTML={{ __html: member.details[language] }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}